import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export interface IContractFilters {
  status: string[];
  dateFrom: string;
  dateTo: string;
  team: string[];
}

interface ContractFilterDialogProps {
  open: boolean;
  onClose: () => void;
  filters: IContractFilters;
  onApply: (filters: IContractFilters) => void;
}

const statusOptions = [
  { value: "Em Execução", label: "Em Execução" },
  { value: "Vencendo", label: "Vencendo" },
  { value: "Solicitado", label: "Solicitado" },
  { value: "Concluído", label: "Concluído" },
];

const teamOptions = [
  { value: "Produtora", label: "Produtora" },
  { value: "UI/UX Designer", label: "UI/UX Designer" },
  { value: "Diretor", label: "Diretor" },
  { value: "Roteirista", label: "Roteirista" },
  { value: "Elenco", label: "Elenco" },
];

export default function ContractFilterDialog({
  open,
  onClose,
  filters,
  onApply,
}: ContractFilterDialogProps) {
  const [localFilters, setLocalFilters] = useState<IContractFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, open]);

  const handleStatusToggle = (status: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }));
  };

  const handleTeamToggle = (team: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      team: prev.team.includes(team)
        ? prev.team.filter((t) => t !== team)
        : [...prev.team, team],
    }));
  };

  const handleApply = () => {
    onApply(localFilters);
  };

  const handleClear = () => {
    const clearedFilters: IContractFilters = {
      status: [],
      dateFrom: "",
      dateTo: "",
      team: [],
    };
    setLocalFilters(clearedFilters);
    onApply(clearedFilters);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-darkblue border-gray-600 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white text-lg font-semibold">
            Filtros
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Status Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Status</p>
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`status-${option.value}`}
                  checked={localFilters.status.includes(option.value)}
                  onCheckedChange={() => handleStatusToggle(option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </div>

          {/* Team Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Equipe/Função</p>
            <div className="space-y-2">
              {teamOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`team-${option.value}`}
                  checked={localFilters.team.includes(option.value)}
                  onCheckedChange={() => handleTeamToggle(option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </div>

          {/* Date Range Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Período</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="dateFrom" className="text-xs text-gray-400 mb-1 block">
                  De
                </label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={localFilters.dateFrom}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
              <div>
                <label htmlFor="dateTo" className="text-xs text-gray-400 mb-1 block">
                  Até
                </label>
                <Input
                  id="dateTo"
                  type="date"
                  value={localFilters.dateTo}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClear} className="flex-1">
            Limpar
          </Button>
          <Button onClick={handleApply} className="flex-1">
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
